import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Package, MapPin, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";

import { get, patch } from "@/lib/api-client";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { Select } from "@/components/ui/Select";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import type { Order } from "@/types/order";

export function OrderDetailAdminPage() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data: order, isLoading } = useQuery({
    queryKey: ["admin-order", orderId],
    queryFn: () => get<Order>(`/orders/orders/${orderId}`),
    enabled: !!orderId,
  });

  // Action to update general Order Status
  const updateStatus = useMutation({
    mutationFn: (newStatus: string) => patch(`/admin/orders/${orderId}/status`, { status: newStatus }),
    onSuccess: () => {
      toast.success("Order status updated!");
      qc.invalidateQueries({ queryKey: ["admin-order", orderId] });
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
    },
  });

  // 🛠️ NEW: Action to update Shipping Status
  const updateShipping = useMutation({
    mutationFn: (newStatus: string) => patch(`/admin/orders/${orderId}/status`, { shipping_status: newStatus }),
    onSuccess: () => {
      toast.success("Shipping status updated!");
      qc.invalidateQueries({ queryKey: ["admin-order", orderId] });
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
    },
  });

  if (isLoading || !order) return <Skeleton className="h-96 w-full rounded-xl" />;

  return (
    <div className="space-y-6 pb-10 max-w-5xl mx-auto">
      <button onClick={() => nav(-1)} className="text-sm text-surface-500 hover:text-brand-600 inline-flex items-center gap-1 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Orders
      </button>

      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-surface-200 shadow-sm">
        <div>
          <div className="text-2xs uppercase tracking-wide text-surface-500 font-semibold mb-1">Order Details</div>
          <h1 className="text-3xl font-extrabold text-surface-900 font-mono tracking-tight">{order.order_id}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-surface-500">Order:</span> <StatusPill status={order.status} />
            <span className="text-xs text-surface-500 ml-2">Payment:</span> <StatusPill status={order.payment_status} />
            <span className="text-xs text-surface-500 ml-2">Shipping:</span> <StatusPill status={order.shipping_status} />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 bg-surface-50 p-4 rounded-xl border border-surface-200">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="text-xs font-semibold text-surface-700 w-20">Order:</div>
            <Select 
              value={order.status}
              onChange={(e) => updateStatus.mutate(e.target.value)}
              className="w-40"
              options={[
                { value: "draft", label: "Draft" },
                { value: "pending_payment", label: "Pending Payment" },
                { value: "payment_verification_pending", label: "Verifying Payment" },
                { value: "completed", label: "Completed" },
                { value: "rejected", label: "Rejected" },
                { value: "cancelled", label: "Cancelled" },
              ]}
            />
          </div>
          
          <div className="hidden sm:block w-px h-8 bg-surface-200" />
          
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="text-xs font-semibold text-surface-700 w-20"><Truck className="inline w-3 h-3 mr-1"/> Shipping:</div>
            <Select 
              value={order.shipping_status}
              onChange={(e) => updateShipping.mutate(e.target.value)}
              className="w-40"
              options={[
                { value: "pending", label: "Pending" },
                { value: "processing", label: "Processing" },
                { value: "shipped", label: "Shipped" },
                { value: "delivered", label: "Delivered" },
                { value: "returned", label: "Returned" },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* LEFT: Products Table */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-2xl border-0 shadow-elevate-1 ring-1 ring-surface-200/50 overflow-hidden">
            <CardHeader className="bg-surface-50/50 border-b border-surface-100">
              <CardTitle>Products Ordered</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <Table>
                <THead>
                  <TR>
                    <TH>Product</TH>
                    <TH>SKU</TH>
                    <TH className="text-center">Qty</TH>
                    <TH className="text-right">Line Total</TH>
                  </TR>
                </THead>
                <tbody>
                  {order.items.map((i) => (
                    <TR key={i.id}>
                      <TD className="font-semibold text-surface-900">{i.product_name}</TD>
                      <TD className="font-mono text-xs text-surface-500">{i.variant_sku}</TD>
                      <TD className="text-center font-bold">{i.quantity}</TD>
                      <TD className="text-right font-extrabold text-brand-700">{formatCurrency(i.line_total)}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
              <div className="p-5 bg-surface-50 flex justify-between items-center border-t border-surface-200">
                <span className="text-sm font-bold uppercase tracking-wider text-surface-500">Total Order Amount</span>
                <span className="text-2xl font-extrabold text-brand-700 tracking-tight">{formatCurrency(order.total)}</span>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* RIGHT: Customer & Shipping Details */}
        <div className="space-y-6">
          <Card className="rounded-2xl border-0 shadow-elevate-1 ring-1 ring-surface-200/50">
            <CardHeader className="bg-surface-50/50 border-b border-surface-100">
              <CardTitle><MapPin className="w-4 h-4 text-brand-500" /> Shipping Address</CardTitle>
            </CardHeader>
            <CardBody className="text-sm text-surface-800 whitespace-pre-wrap leading-relaxed">
              {order.notes ? order.notes.replace("Shipping Address: ", "") : "No address provided."}
            </CardBody>
          </Card>

          <Card className="rounded-2xl border-0 shadow-elevate-1 ring-1 ring-surface-200/50">
            <CardHeader className="bg-surface-50/50 border-b border-surface-100">
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-500">Placed At</span>
                <span className="font-semibold text-surface-900">{formatDateTime(order.placed_at)}</span>
              </div>
              {order.completed_at && (
                <div className="flex justify-between">
                  <span className="text-surface-500">Completed At</span>
                  <span className="font-semibold text-surface-900">{formatDateTime(order.completed_at)}</span>
                </div>
              )}
              {order.cancelled_at && (
                <div className="flex justify-between">
                  <span className="text-danger-500">Cancelled At</span>
                  <span className="font-semibold text-danger-700">{formatDateTime(order.cancelled_at)}</span>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}